import { asLink } from "@prismicio/helpers";
import clsx from "clsx";
import CustomLink from "./CustomLink";
import { type MouseEventHandler } from "react";

export default function CallToAction({
  to,
  children,
  isFilled = true,
  isOnDarkBackground = false,
  smallText = null,
  isButton = false,
  buttonCallback = null,
  isPartOfForm = false,
  className,
}: {
  to?: string | null;
  children: React.ReactNode;
  isFilled?: boolean;
  isOnDarkBackground?: boolean;
  smallText?: string | null;
  isButton?: boolean;
  buttonCallback?: MouseEventHandler<HTMLButtonElement> | null;
  isPartOfForm?: boolean;
  className?: string;
}) {
  const filledButtonTextStyles = isOnDarkBackground
    ? "text-black"
    : "text-white";

  const outlineButtonTextStyles = isOnDarkBackground
    ? "text-white"
    : "text-black group-hover:text-white";

  const filledButtonBackgroundStyles = isOnDarkBackground
    ? "bg-white border-white"
    : "bg-blue border-blue group-hover:bg-black group-hover:border-black";
  const outlineButtonBackgroundStyles = isOnDarkBackground
    ? "border-white"
    : "border-black group-hover:bg-black";

  //We have to check the given props to render on, because we already render
  //some decorative markup before we pass to custom link component
  const linkUrl = typeof to === "string" ? to : asLink(to);
  const linkText = children;
  if (isButton && !buttonCallback && !isPartOfForm) return null;
  if (!isButton && (!linkUrl || !linkText)) return null;

  if (isButton) {
    const content = children;
    if (!content) return null;

    return (
      <button
        className={clsx(
          "group relative inline-block cursor-pointer text-cta",
          className && className,
        )}
        onClick={buttonCallback ?? undefined}
      >
        {/* The background of the button, this is seperate so we can visually make it smaller */}
        <span
          className={clsx(
            "absolute left-0 top-0 h-full w-full origin-center scale-x-100 scale-y-100 border px-8 py-4 transition-all group-hover:scale-x-[0.98] group-hover:scale-y-[0.96] group-hover:rounded",
            isFilled
              ? filledButtonBackgroundStyles
              : outlineButtonBackgroundStyles,
          )}
        ></span>
        <span
          className={clsx(
            "relative inline-block px-8 py-4 font-nb-bold text-cta leading-none transition-all",
            isFilled ? filledButtonTextStyles : outlineButtonTextStyles,
          )}
        >
          {content}
        </span>
      </button>
    );
  }

  return (
    <span
      className={clsx(
        "group relative inline-block cursor-pointer",
        className && className,
      )}
    >
      {/* The background of the button, this is seperate so we can visually make it smaller */}
      <span
        className={clsx(
          "absolute left-0 top-0 h-full w-full origin-center scale-x-100 scale-y-100 border px-8 py-4 transition-all group-hover:scale-x-[0.98] group-hover:scale-y-[0.96] group-hover:rounded",
          isFilled
            ? filledButtonBackgroundStyles
            : outlineButtonBackgroundStyles,
        )}
      ></span>
      <CustomLink
        to={to}
        className={clsx(
          "relative inline-block px-8 py-4 font-nb-bold text-cta leading-none transition-all",
          isFilled ? filledButtonTextStyles : outlineButtonTextStyles,
        )}
        childrenSubText={smallText}
      >
        {children}
      </CustomLink>
    </span>
  );
}
